/**
 * Utilities for smooth scrolling behavior with cross-browser support.
 *
 * What: Provides polyfill for smooth scroll and utilities for programmatic scrolling to sections.
 * Why: Ensures consistent smooth scroll experience across all browsers, especially iOS Safari.
 * When: Update when changing scroll behavior, duration, or easing functions.
 */

/**
 * Check if native smooth scroll is supported
 */
function supportsNativeSmoothScroll(): boolean {
  if (typeof window === 'undefined') return false;
  return 'scrollBehavior' in document.documentElement.style;
}

/**
 * Easing function for smooth scroll animation
 */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
}

/**
 * Polyfill for smooth scrolling to a position
 */
function smoothScrollTo(targetPosition: number, duration = 600): void {
  const startPosition = window.scrollY || window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime: number | null = null;

  function animation(currentTime: number) {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const ease = easeInOutCubic(progress);

    window.scrollTo(0, startPosition + distance * ease);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  }

  requestAnimationFrame(animation);
}

interface ScrollToSectionOptions {
  offset?: number;
  duration?: number;
}

/**
 * Scroll to a section with smooth behavior
 */
export function scrollToSection(
  sectionId: string,
  options: ScrollToSectionOptions = {}
): void {
  const { offset = 0, duration = 600 } = options;

  const element = document.getElementById(sectionId);
  if (!element) {
    console.warn(`Section with id "${sectionId}" not found`);
    return;
  }

  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  const offsetPosition = elementPosition - offset;

  // Use native smooth scroll if supported, otherwise use polyfill
  if (supportsNativeSmoothScroll()) {
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  } else {
    smoothScrollTo(offsetPosition, duration);
  }
}

/**
 * Initialize smooth scroll behavior for anchor links
 */
export function initSmoothScroll(headerOffset = 80): (() => void) | undefined {
  if (typeof window === 'undefined') return;

  // Add CSS for browsers that support it natively
  if (supportsNativeSmoothScroll()) {
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  // Handle hash changes in URL
  const handleHashChange = () => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.substring(1); // Remove the '#'
      // Small delay to ensure page is loaded
      setTimeout(() => {
        scrollToSection(sectionId, { offset: headerOffset });
      }, 100);
    }
  };

  // Handle initial hash on page load
  if (window.location.hash) {
    handleHashChange();
  }

  // Listen for hash changes
  window.addEventListener('hashchange', handleHashChange);

  return () => {
    window.removeEventListener('hashchange', handleHashChange);
  };
}

/**
 * Get the appropriate scroll margin for a section based on viewport size
 */
export function getScrollMargin(isMobile: boolean): number {
  // Mobile needs more margin due to sticky nav
  return isMobile ? 120 : 96;
}
